<template>
  <div class="station-cutting-batch-page station-batch-droppable-container" @click="focusBarcodeEntry()">
      
    <div class="station-batch-header">
      <h2 class="text-muted">Batch Panels</h2>
      
      <h2 class="text-muted text-center mt-4" v-if="!hasBatchPanels">Drag panels or orders from the schedule into this area to setup a batch</h2>
    </div>

    <div class="station-panel-schedule-container">
      <div class="station-batch-updating" v-if="updatingServer || loading">
        <div>
          <h3 v-if="updatingServer">UPDATING</h3>
          <h3 v-if="loading">LOADING</h3>
          <Spinner />
        </div>
      </div>

      <div class="station-batch-dropzone" v-show="showItemDrop">Drop panels / orders here to add to the batch</div>

      <div class="station-panel-schedule-list">
        <div class="row no-gutters" v-for="panel of batchPanelList" :key="`panel-${panel.id}`">
          <div class="panel-list-row-area">
            <StationScheduleRow
              :id="panel.id"
              :stockId="panel.stock.id"
              :stockName="panel.stock.name"
              :panelType="panel.type"
              :panelWidth="panel.panelWidth"
              :panelHeight="panel.panelHeight"
              :grainDirection="panel.orderItem.grainDirection"
              :stockFaceSideUpType="panel.orderItem.stockFaceSideUpType"
              :orderSeqNum="panel.order.sequenceNumber"
              :orderCustomer="panel.order.contact.name"
              :dueDate="panel.dueDate"
              :statusCurrent="panel.currentTaskName"
              :statusNext="panel.nextTaskName"
              :currentStation="panel.currentStation"
              :isReady="panel.isReady"
              :isCurrentTaskInProduction="panel.isCurrentTaskInProduction"
              :isCurrentTaskAtStation="panel.isCurrentTaskAtStation"
              :panelBarcode="panel.barcode"
              :panelIndex="panel.panelIndex"
              :panelQuantity="panel.orderItem.quantity"
              :onHold="panel.order.isOnHold"
              :isStoppedRecut="panel.isStoppedRecut"
              :isStoppedCancelled="panel.isStoppedCancelled"
              :optimiserSheetId="panel.optimiserSheetId"
              :optimiserSheetStatus="panel.optimiserSheetStatus"
              @click="listPanelClicked($event, panel)"
              :ref="`panel-${panel.id}`">

              <template v-slot:labels>
                <LabelBadge v-for="(tag, index) of panel.tags" :key="`tag-${index}`" :labelColour="tag.colour" class="text-uppercase">{{tag.name}}</LabelBadge>
                
                <LabelBadge labelClass="label-order-urgent" v-if="panel.isUrgent">Urgent</LabelBadge>
                <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="panel.isRecut">RECUT</LabelBadge>
                <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="panel.isNCR">NCR</LabelBadge>
                <LabelBadge labelClass="label-order-hold" v-if="panel.isOnHold">ON HOLD</LabelBadge>
                <LabelBadge labelClass="label-production-status-recut" v-if="panel.isStoppedRecut">STOP: RECUT</LabelBadge>
                <LabelBadge labelClass="label-order-status-denied" v-if="panel.isStoppedCancelled">STOP: CANCELLED</LabelBadge>
                <LabelBadge labelClass="label-order-status-preProduction" v-if="panel.order.status == OrderStatusTypes.preProduction">PRE-PRODUCTION</LabelBadge>
                <LabelBadge labelClass="label-order-template-required" v-if="panel.isTemplateHandlingRequired">TEMPLATE REQUIRED</LabelBadge>
              </template>

            </StationScheduleRow>
          </div>
          <div class="panel-list-option-area">
            <StationControlPanelButton class="btn-primary" sizeClass="lg" iconStyle="fal" icon="fa-print" v-if="isBatchInCutting" @click="printBatchPanelLabel(panel.id)">Print</StationControlPanelButton>
            <StationControlPanelButton class="btn-primary" sizeClass="lg" iconStyle="fal" icon="fa-ruler-combined" v-if="isPanelEditorViewAvailable(panel)" @click="displayPanelOrderEditorView(panel)">Editor</StationControlPanelButton>
            <StationControlPanelButton class="btn-primary" sizeClass="lg" iconStyle="fal" icon="fa-book" @click="displayPanelOrderDocuments(panel)">Docs</StationControlPanelButton>
            <StationControlPanelButton class="btn-danger" sizeClass="lg" iconStyle="fal" icon="fa-times" v-if="isBatchInEditing" @click="removePanel(panel.id)">Remove</StationControlPanelButton>
          </div>
        </div>
      </div>
    </div>

    <div class="station-batch-options" v-show="hasBatchPanels">
      <StationControlPanelButton class="btn-danger" sizeClass="lg" iconStyle="fal" icon="fa-backspace" @click="clearBatchList" v-if="isBatchInEditing">Clear</StationControlPanelButton>
      <StationControlPanelButton class="btn-danger" sizeClass="lg" iconStyle="fal" icon="fa-times" @click="cancelProductionBatch" v-if="isBatchInCutting">Cancel</StationControlPanelButton>
      <div class="mr-2"></div>
      <StationControlPanelButton class="btn-success" sizeClass="lg" iconStyle="fal" icon="fa-filter" @click="filterClicked" v-if="isBatchInEditing">Filter</StationControlPanelButton>
      <StationControlPanelButton class="btn-success" sizeClass="lg" iconStyle="fal" icon="fa-sort-alt" @click="sortClicked" v-if="isBatchInEditing">Sort</StationControlPanelButton>
      <div class="mr-2" v-if="isBatchInEditing"></div>
      <StationControlPanelButton class="btn-complete" sizeClass="lg" icon="fa-scalpel-path" @click="cutProductionBatch" v-if="isBatchInEditing">Cut</StationControlPanelButton>
      <StationControlPanelButton class="btn-warning" sizeClass="lg" icon="fa-undo" @click="revertProductionBatch" v-if="isBatchInCutting">Revert</StationControlPanelButton>
      <StationControlPanelButton class="btn-primary" sizeClass="lg" icon="fa-print" @click="printBatchPanelListLabels" v-if="isBatchInCutting">Print</StationControlPanelButton>

      <div class="station-batch-control-bar-space"> </div>

      <div class="station-batch-control-scanner">
        <div class="row no-gutters">
          <div class="col-7">
            <div class="station-scanner-entry" v-show="isBatchInCutting">
              <FormInputText ref="panelBarcodeScan" v-model="scanOutBarcodeEntry" placeholder="Barcode" @enter="onScannedBarcode" prependSlot>
                <template v-slot:prepend>
                  <i class="far fa-scanner" v-if="!scanOutSubmitting"></i>
                  <Spinner v-if="scanOutSubmitting" />
                </template>
              </FormInputText>
            </div>
          </div>
          <div class="col-1 pl-2" v-if="isBatchInCutting && scanOutShowResult">
            <StationErrorAnimation :scale="0.40" v-if="scanOutResultType == 'error'" />
            <StationSuccessAnimation :scale="0.40" v-if="scanOutResultType == 'success'" />
          </div>
          <div class="col-4" v-if="isBatchInCutting && scanOutShowResult">
            <h1 class="station-batch-scan-message">{{scanOutResultMessage}}</h1>
          </div>
        </div>
      </div>
    </div>

    <Popover ref="filterPopover" sizeClass="popover-large" :showHeader="true" headerTitle="Filter Panels" placement="top">
      <div class="p-2">
        <div class="row">
          <div class="col-12">
            Filter By Stock<br />
            <FormInputStockCode :appendToBody="true" :stockType="InventoryStockTypes.glass" v-model="filterStockId" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <button class="btn btn-primary btn-block mt-2" @click.prevent="filterRemoveByStock" :disabled="!filterStockId">Apply Stock Filter</button>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <FormHorizontalRule />
            Filter By Height<br />
            <FormInputText type="number" addon="mm" v-model="filterHeight" />
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <button class="btn btn-primary btn-block mt-2" @click.prevent="filterRemoveSmallerThanHeight" :disabled="!filterHeight">Remove Smaller Than</button>
          </div>
          <div class="col-6">
            <button class="btn btn-primary btn-block mt-2" @click.prevent="filterRemoveLargerThanHeight" :disabled="!filterHeight">Remove Larger Than</button>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <FormHorizontalRule />
            Filter By Width<br />
            <FormInputText type="number" addon="mm" v-model="filterWidth" />
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <button class="btn btn-primary btn-block mt-2" @click.prevent="filterRemoveSmallerThanWidth" :disabled="!filterWidth">Remove Smaller Than</button>
          </div>
          <div class="col-6">
            <button class="btn btn-primary btn-block mt-2" @click.prevent="filterRemoveLargerThanWidth" :disabled="!filterWidth">Remove Larger Than</button>
          </div>
        </div>
      </div>
    </Popover>

    <Popover ref="sortPopover" sizeClass="popover-medium" :showHeader="true" headerTitle="Sort Panels" placement="top">
      <div class="p-2 row">
        <div class="col-4">
          <button class="btn btn-success station-popover-button btn-block" @click.prevent="setSortOrderDefault"><i class="fal fa-sort-numeric-down" /><br />Default</button>
        </div>
        <div class="col-4">
          <button class="btn btn-success station-popover-button btn-block" @click.prevent="setSortOrderHeight"><i class="fal fa-sort-amount-down" /><br />Height</button>
        </div>
        <div class="col-4">
          <button class="btn btn-success station-popover-button btn-block" @click.prevent="setSortOrderWidth"><i class="fal fa-sort-amount-down" /><br />Width</button>
        </div>
      </div>
    </Popover>

  </div>
</template>



<style lang="scss" scoped>
.station-cutting-batch-page {
  flex: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items:stretch;
  overflow-x: hidden;
}
.station-batch-dropzone {
  box-sizing: border-box;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  font-size: 26px;
  background: rgba(32, 32, 32, .6);
  color: #FFFFFF;
  border: 3px dashed #808080;
  border-radius: 10px;
  z-index: 10;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}
.station-batch-updating {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  font-size: 26px;
  background: rgba(32, 32, 32, .6);
  color: #FFFFFF;
  z-index: 10;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}
.station-panel-schedule-container {
  position: relative;
  flex: 1;  
}
.station-panel-schedule-list {
  position: absolute;
  width: 100%;
  top: 0;
  bottom: 0;
  overflow-y:scroll;  
}
.panel-list-row-area {
  flex: 1;
  padding: 0 0.25rem 0 0.5rem;
}
.station-batch-container {
  position: absolute;
  top: 0; left: 0;
  right: 0; bottom: 0;
  overflow-x: hidden;
  overflow-y: auto;
  font-size: 13px;
  display: flex;
}
.station-batch-header {
  height: 70px;
}
.station-batch-options {
  padding-top: 10px;
  height: 70px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}
.panel-list-option-area {
  height: 100%;
  width: 210px;
  text-align: right;
  margin: auto 0;
}
.station-batch-control-bar-space {
  width: 7%;  
}

.station-batch-control-scanner {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}
.station-batch-control-scanner > .row {
  width: 100%;
}
.station-batch-scan-message {
  font-weight: 100;
  line-height: 1.2;
  font-size: 30px;
  margin: 0;
}

</style>

<script>
import _ from 'lodash'
import buildUrl from 'build-url'

import formMixin from '@/mixins/form'
import Utils from '@/utils/utils'
import EventBus from '@/utils/eventBus'

import ProductionStationMixin from '@/apps/glass-production-station/src/mixins/productionStation'

import StationControlPanelButton from '@/apps/glass-production-station/src/components/buttons/stationControlPanelButton'
import StationScheduleRow from '@/apps/glass-production-station/src/components/schedule/stationScheduleRow'
import Popover from '@/components/popover.vue'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import StationErrorAnimation from '@/apps/glass-production-station/src/components/animations/stationErrorAnimation'
import StationSuccessAnimation from '@/apps/glass-production-station/src/components/animations/stationSuccessAnimation'

import { LinkTypes, OrderStatusTypes, InventoryStockTypes, ProductionBatchStatusTypes } from '@/store/types'

export default {
  name: 'CuttingBatchPage',
  mixins: [formMixin, ProductionStationMixin],
  components: {
    StationControlPanelButton,
    StationScheduleRow,
    Popover,
    FormInputStockCode,
    StationErrorAnimation,
    StationSuccessAnimation
  },
  props: {
    productionStage: Object,
    productionStation: Object,
    showItemDrop: Boolean
  },
  data() {
    return {
      loading: false,
      updatingServer: false,
      activeProductionBatch: null,

      filterStockId: 0,
      filterWidth: 0,
      filterHeight: 0,

      scanOutBarcodeEntry: '',
      scanOutSubmitting: false,
      scanOutShowResult: false,
      scanOutResultType: '',
      scanOutResultMessage: '',
    }
  },
  watch: {
    activeProductionBatch: function() {
      var panelCount = 0

      if(this.activeProductionBatch && this.activeProductionBatch.productionBatchPanels)
        panelCount = this.activeProductionBatch.productionBatchPanels.length

      this.$emit('panelCountUpdated', panelCount)
    }
  },
  computed: {
    batchPanelList() {
      if(!this.activeProductionBatch)
        return []

      return this.activeProductionBatch.productionBatchPanels.map(batchPanel => {
        return batchPanel.productionPanel
      })
    },
    hasBatchPanels() {
      return this.batchPanelList.length > 0
    },
    isBatchInEditing() {
      return (this.activeProductionBatch && this.activeProductionBatch.status == ProductionBatchStatusTypes.editing)
    },
    isBatchInCutting() {
      return (this.activeProductionBatch && this.activeProductionBatch.status == ProductionBatchStatusTypes.cutting)
    }
  },
  created() {
    this.OrderStatusTypes = OrderStatusTypes
    this.InventoryStockTypes = InventoryStockTypes
    this.ProductionBatchStatusTypes = ProductionBatchStatusTypes
  },
  mounted() {
    this.focusBarcodeEntry()
  },
  methods: {
    // Page Selected - Called when this cutting batch page is selected to be shown
    pageSelected() {
      this.requestData()
    },
    addPanelToBatchPage(panelId) {
      this.addPanelsWithFilter({ productionPanelId: panelId, isAvailableOnly: 0 });      
    },
    addOrderToBatchPage(orderId) {
      this.addPanelsWithFilter({ orderId: orderId, isAvailableOnly: 1 });      
    },
    listPanelClicked(event, panel) {
      let panelComponent = this.$refs[`panel-${panel.id}`][0]

      this.$emit('panelClick', { panel: panel, target: panelComponent.$el })
    },
    requestData() {
      this.loading = true
      this.$apiService.glassProductionStationService.getStationProductionBatch(this.productionStation.id)
      .then(response => {
        this.activeProductionBatch = response.data

        this.$nextTick(() => {
          this.focusBarcodeEntry()
        })
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Error accessing production batch')
      })
      .finally(() => {
        this.loading = false
      })
    },
    addPanelsWithFilter(filterParams) {
      var params = {
        start: 0,
        length: 10000,
        draw: 1,
        isAvailableOnly: filterParams.isAvailableOnly,
        isOptimiserAvailableOnly: 0,
        ignoreOnHold: 0,
        searchString: null,
        productionPanelId: filterParams.productionPanelId,
        orderId: filterParams.orderId
      }

      var updatePanelList = [...this.batchPanelList]

      // Uses schedule list to ensure we don't add panels that are not required at this station / stage
      // Or have not yet been completed at this stage
      this.panelScheduleError = ''
      this.$apiService.glassProductionStationService.getPanelScheduleForStation(this.productionStation.id, params)
      .then(response => {

        // Add in the panels
        if(response.data.groupList) {
          response.data.groupList.forEach(group => {
            group.panelList.forEach(panel => {

              // Don't add if already in the list
              if(updatePanelList.find(listPanel => listPanel.id == panel.id) == null)
                updatePanelList.push(_.cloneDeep(panel))
            })
          })
        }

        this.updateProductionBatch(updatePanelList)
      })
      .catch(err => {
        console.error(err);

        this.displayNetworkError(err, 'Error getting panel/order data')
      })
    },
    removePanel(id) {
      var updatePanelList = this.batchPanelList.filter(panel => panel.id != id)

      this.updateProductionBatch(updatePanelList)
    },
    clearBatchList() {
      this.updateProductionBatch([])
    },
    updateProductionBatch(panelList) {
      // Send update to the production batch
      var batchData = {
        panelIdList: panelList.map(panel => panel.id)
      }

      this.updatingServer = true
      this.$apiService.glassProductionStationService.updateStationProductionBatch(this.productionStation.id, batchData)
      .then(response => {
        this.activeProductionBatch = response.data
      })
      .catch(err => {
        console.error(err);
        this.displayNetworkError(err, 'Error updating batch')
      })
      .finally(() => {
        this.updatingServer = false
      })
    },
    cancelProductionBatch() {
      this.confirmDeleteMessage('Cancel Production Batch?', '', { html: 'Are you sure you want to cancel the production batch?<br />This step cannot be undone' })
      .then((success) => {
        if(success) {
          this.updatingServer = true
          this.$apiService.glassProductionStationService.cancelStationProductionBatch(this.productionStation.id)
          .then(response => {
            
            this.activeProductionBatch = null
            this.successToast('Batch cancelled')
          })
          .catch(err => {
            console.error(err)
            this.displayNetworkError(err, 'Error cancelling production batch')
          })
          .finally(() => {
            this.updatingServer = false
          })
        }          
      })
    },
    cutProductionBatch() {
      this.confirmDeleteMessage('Begin Cutting?', '', { html: 'Are you sure you want to begin cutting the production batch?' })
      .then((success) => {
        if(success) {
          this.updatingServer = true
          this.$apiService.glassProductionStationService.startCuttingProductionBatch(this.productionStation.id)
          .then(response => {
            
            this.activeProductionBatch = response.data
            this.successToast('Cutting started')

            this.$nextTick(() => {
              this.focusBarcodeEntry()
            })
          })
          .catch(err => {
            console.error(err)
            this.displayNetworkError(err, 'Error cutting production batch')
          })
          .finally(() => {
            this.updatingServer = false
          })
        }          
      })
    },
    revertProductionBatch() {
      this.confirmDeleteMessage('Revert Cutting?', '', { html: 'Are you sure you want to revert cutting the production batch?<br />Panels scanned out already will not be reverted' })
      .then((success) => {
        if(success) {
          this.updatingServer = true
          this.$apiService.glassProductionStationService.revertCuttingProductionBatch(this.productionStation.id)
          .then(response => {
            
            this.activeProductionBatch = response.data
            this.successToast('Cutting reverted')
          })
          .catch(err => {
            console.error(err)
            this.displayNetworkError(err, 'Error reverting production batch')
          })
          .finally(() => {
            this.updatingServer = false
          })
        }          
      })
    },
    printBatchPanelListLabels() {
      // Print complete batch labels
      var labelURL = `/production/stations/${this.productionStation.id}/productionbatch/panellabels`
      var printParams = { download: true, label: true }

      var params = $.param(printParams)
      this.$apiService.reportingService.generateReportSignedURL(`${labelURL}?${params}`)
      .then(response => {
        setTimeout(() => {
          this.displayPopupAlert('success', `Labels Printed`, `Labels printed for panel list`)
        }, 50)

        window.location.href = response.data.url
      })
    },
    printBatchPanelLabel(panelId) {
      // Print 1 label
      var labelURL = `/production/panels/labels`
      var printParams = { download: true, label: true, panelIDList: [panelId] }

      var params = $.param(printParams)
      this.$apiService.reportingService.generateReportSignedURL(`${labelURL}?${params}`)
      .then(response => {
        setTimeout(() => {
          this.displayPopupAlert('success', `Label Printed`, `Label printed for panel`)
        }, 50)

        window.location.href = response.data.url
      })
    },
    filterClicked(evt) {
      this.filterStockId = 0
      this.filterHeight = 0
      this.filterWidth = 0

      this.$nextTick(() => {
        this.$refs.filterPopover.displayFromElement(evt.target)
      })
    },
    sortClicked(evt) {
      this.$nextTick(() => {
        this.$refs.sortPopover.displayFromElement(evt.target)
      })
    },
    setSortOrderDefault() {
      var updatePanelList = this.batchPanelList

      updatePanelList.sort((panel1, panel2) => {
        // Order id first, then panel id
        if(panel1.orderId == panel2.orderId)
          return panel1.id - panel2.id

        return panel1.orderId - panel2.orderId        
      })
      
      this.$nextTick(() => {
        this.$refs.sortPopover.hidePopover()
      })
      this.updateProductionBatch(updatePanelList)
    },
    setSortOrderWidth() {
      var updatePanelList = this.batchPanelList

      updatePanelList.sort((panel1, panel2) => {
        return panel2.panelWidth - panel1.panelWidth
      })

      this.$nextTick(() => {
        this.$refs.sortPopover.hidePopover()
      })

      this.updateProductionBatch(updatePanelList)
    },
    setSortOrderHeight() {
      var updatePanelList = [...this.batchPanelList]

      updatePanelList.sort((panel1, panel2) => {
        return panel2.panelHeight - panel1.panelHeight
      })

      this.$nextTick(() => {
        this.$refs.sortPopover.hidePopover()
      })

      this.updateProductionBatch(updatePanelList)
    },
    isPanelEditorViewAvailable(panel) {
      if(!this.userHasReadPermission(this.PermissionTypes.OrderEditorPermissionTypes.orderEditor))
        return false
      if(panel.orderItem && panel.orderItem.isEditorGeneratedItem)
        return true

      return false
    },
    filterRemoveByStock() {
      if(!this.filterStockId)
        return

      var updatePanelList = this.batchPanelList.filter(panel => panel.stockId == this.filterStockId)

      this.$nextTick(() => {
        this.$refs.filterPopover.hidePopover()
      })

      this.updateProductionBatch(updatePanelList)
    },
    filterRemoveSmallerThanHeight() {
      if(!this.filterHeight)
        return

      var updatePanelList = this.batchPanelList.filter(panel => panel.panelHeight >= parseInt(this.filterHeight))

      this.$nextTick(() => {
        this.$refs.filterPopover.hidePopover()
      })

      this.updateProductionBatch(updatePanelList)
    },
    filterRemoveLargerThanHeight() {
      if(!this.filterHeight)
        return

      var updatePanelList = this.batchPanelList.filter(panel => panel.panelHeight <= parseInt(this.filterHeight))

      this.$nextTick(() => {
        this.$refs.filterPopover.hidePopover()
      })

      this.updateProductionBatch(updatePanelList)
    },
    filterRemoveSmallerThanWidth() {
      if(!this.filterWidth)
        return

      var updatePanelList = this.batchPanelList.filter(panel => panel.panelWidth >= parseInt(this.filterWidth))

      this.$nextTick(() => {
        this.$refs.filterPopover.hidePopover()
      })

      this.updateProductionBatch(updatePanelList)
    },
    filterRemoveLargerThanWidth() {
      if(!this.filterWidth)
        return

      var updatePanelList = this.batchPanelList.filter(panel => panel.panelWidth <= parseInt(this.filterWidth))

      this.$nextTick(() => {
        this.$refs.filterPopover.hidePopover()
      })

      this.updateProductionBatch(updatePanelList)
    },
    onScannedBarcode() {
      if(this.scanOutBarcodeEntry.trim().length == '') {
        this.scanOutBarcodeEntry = ''
        return
      }

      this.scanOutSubmitting = true
      this.scanOutShowResult = false

      // Run the scan function for the batch
      this.$apiService.glassProductionStationService.scanStationProductionBatch(this.productionStation.id, { barcode: this.scanOutBarcodeEntry })
      .then(response => {
        this.scanOutSubmitting = false
        this.activeProductionBatch = response.data

        // Check if batch finished
        if(!this.activeProductionBatch) {
          this.displayPopupAlert('success', 'Batch Completed')
        } else {

          // Show success result for panels
          this.$nextTick(() => {
            this.scanOutShowResult = true
            this.scanOutResultType = 'success'
            this.scanOutResultMessage = 'Panel Scanned Out'
          })
        }

        this.scanOutBarcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })
      .catch(err => {
        this.scanOutSubmitting = false
        let errorInfo = this.processFormResponse(err)
        this.scanOutBarcodeEntry = ''

        // Show errors as a popup as they will contain more details
        this.displayPopupAlert('error', 'Scanning Error!', errorInfo.errorMessage, { hasTimeout: true })

        this.$refs.panelBarcodeScan.focus()
      })
    },
    focusBarcodeEntry() {
      if(this.$refs.panelBarcodeScan && this.isBatchInCutting) {
        this.$refs.panelBarcodeScan.select()
        this.$refs.panelBarcodeScan.focus()
      }
    }
    

  }
}
</script>